import { useAuth } from '@/hooks/useAuth';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { getStoredAuthSession } from '@/store/auth/authStorage';
import { hasStoredReferentials } from '@/store/referentials/referentials.storage';
import { useQuery } from '@tanstack/react-query';

interface OfflineDegradedModeResult {
  isOfflineDegradedMode: boolean;
  canUseOfflineReportCreation: boolean;
  isOfflineModeResolving: boolean;
}

export const useOfflineDegradedMode = (): OfflineDegradedModeResult => {
  const { status } = useAuth();
  const { isOnline, isNetworkStatusPending } = useNetworkStatus();

  const storedSessionQuery = useQuery({
    queryKey: ['auth', 'stored-session'],
    queryFn: getStoredAuthSession,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const storedReferentialsQuery = useQuery({
    queryKey: ['referentials', 'stored-presence'],
    queryFn: hasStoredReferentials,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const hasStoredSession = Boolean(storedSessionQuery.data?.refreshToken);
  const hasReferentialsCache = Boolean(storedReferentialsQuery.data);
  const canUseOfflineReportCreation =
    status === 'unauthenticated' && hasStoredSession && hasReferentialsCache;

  const isOfflineModeResolving =
    status === 'unauthenticated' &&
    (storedSessionQuery.isPending ||
      storedReferentialsQuery.isPending ||
      (canUseOfflineReportCreation && isNetworkStatusPending));

  return {
    canUseOfflineReportCreation,
    isOfflineDegradedMode: canUseOfflineReportCreation && !isOnline,
    isOfflineModeResolving,
  };
};
